// components/MyCases.js
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";

const MyCases = ({ userId }) => {
    const [reportedCases, setReportedCases] = useState([]);
    const [casesAgainstMe, setCasesAgainstMe] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchReported, setSearchReported] = useState("");
    const [searchAgainst, setSearchAgainst] = useState("");
    const [currentPageReported, setCurrentPageReported] = useState(1);
    const [currentPageAgainst, setCurrentPageAgainst] = useState(1);
    const itemsPerPage = 10;
    const token = localStorage.getItem("token");

    // ✅ Helper for Status Pills
    const getStatusBadge = (status) => {
        const baseClass = "px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ";
        switch (status?.toLowerCase()) {
            case 'resolved':
                return <span className={baseClass + "bg-green-100 text-green-800 border border-green-200"}>Resolved</span>;
            case 'dismissed':
                return <span className={baseClass + "bg-red-100 text-red-800 border border-red-200"}>Dismissed</span>;
            case 'pending':
            default:
                return <span className={baseClass + "bg-yellow-100 text-yellow-800 border border-yellow-200"}>Pending</span>;
        }
    };

    useEffect(() => {
        if (!userId) return;

        const fetchCases = async () => {
            setLoading(true);
            try {
                const [reportedRes, againstRes] = await Promise.all([
                    axios.get(
                        `https://ims.holdthechild.net/api/disciplinary-cases?reportedBy=${userId}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    ),
                    axios.get(
                        `https://ims.holdthechild.net/api/disciplinary-cases?staffId=${userId}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    ),
                ]);

                setReportedCases(reportedRes.data.results || []);
                setCasesAgainstMe(againstRes.data.results || []);
                setLoading(false);
            } catch (err) {
                setLoading(false);
                toast.error("Failed to load your cases.");
            }
        };

        fetchCases();
    }, [userId, token]);

    // Filter and paginate
    const filterAndPaginate = (cases, search, currentPage) => {
        const q = search.trim().toLowerCase();
        const filtered = q
            ? cases.filter(
                (c) =>
                    (c.staffName?.toLowerCase().includes(q) || "") ||
                    (c.offense?.toLowerCase().includes(q) || "") ||
                    (c.status?.toLowerCase().includes(q) || "")
            )
            : cases;

        const start = (currentPage - 1) * itemsPerPage;
        const paginated = filtered.slice(start, start + itemsPerPage);

        const totalPages = Math.ceil(filtered.length / itemsPerPage);

        return { paginated, totalPages };
    };

    const renderTable = (cases, title, search, setSearch, currentPage, setCurrentPage) => {
        const { paginated, totalPages } = filterAndPaginate(cases, search, currentPage);

        return (
            <div className="bg-white p-6 rounded-lg shadow mt-6">
                <h3 className="text-lg font-semibold mb-4">{title}</h3>

                {/* Search */}
                <input
                    type="search"
                    placeholder="Search by staff, offense, or status"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full sm:max-w-xs rounded border border-gray-300 px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />

                {loading ? (
                    <p>Loading cases...</p>
                ) : paginated.length === 0 ? (
                    <p className="text-gray-500">No cases found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="bg-orange-100 text-gray-600 text-left">
                                    <th className="px-4 py-2 text-orange-600">Staff Involved</th>
                                    <th className="px-4 py-2 text-orange-600">Offense</th>
                                    <th className="px-4 py-2 text-orange-600">Date Reported</th>
                                    <th className="px-4 py-2 text-orange-600">Status</th>
                                    <th className="px-4 py-2 text-orange-600">Action Taken</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((c, idx) => (
                                    <tr key={c._id || idx} className="border-b hover:bg-gray-50">
                                        <td className="px-4 py-2">{c.staffName || "N/A"}</td>
                                        <td className="px-4 py-2">{c.offense || "N/A"}</td>
                                        <td className="px-4 py-2">{moment(c.dateReported).format("MMM D, YYYY")}</td>
                                        <td className="px-4 py-2">
                                            {/* ✅ CHANGED: Replaced text with Status Badge */}
                                            {getStatusBadge(c.status)}
                                        </td>
                                        <td className="px-4 py-2">{c.actionTaken || "N/A"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-4 flex justify-center gap-2">
                        <button
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className={`rounded px-3 py-1 ${currentPage === 1 ? "cursor-not-allowed bg-gray-300" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
                        >
                            Prev
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`rounded px-3 py-1 ${currentPage === i + 1 ? "bg-orange-300 text-orange-900 font-semibold" : "bg-gray-200 hover:bg-gray-300"}`}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className={`rounded px-3 py-1 ${currentPage === totalPages ? "cursor-not-allowed bg-gray-300" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            {renderTable(reportedCases, "Cases I Have Reported", searchReported, setSearchReported, currentPageReported, setCurrentPageReported)}
            {renderTable(casesAgainstMe, "Cases Reported Against Me", searchAgainst, setSearchAgainst, currentPageAgainst, setCurrentPageAgainst)}
        </div>
    );
};

export default MyCases;